import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Typography, TextField, CardContent, Grid, Drawer, Stack, Avatar, CircularProgress, Tooltip, SelectChangeEvent, InputAdornment, Autocomplete, Badge } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import CustomTextField from "../FormFields/CustomTextField";
import DialogClientForm from '../Dialog/CreateClientForm';
import DialogDocumentSettingForm from '../DialogDocumentSettingForm/DialogDocumentSettingForm';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledCard } from '../AccountBasicInfo/AccountBacisInfo.styles';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchInvoiceSeqNumber, addInvoiceCreditNote, fetchInvoiceCreditNoteById, dispatchInvoiceCreditNote, updateInvoiceCreditNote } from '../../store/creditNoteStore/creditNoteThunk';
import { fetchInvoicesByInvNumber } from '../../store/invoiceStore/invoiceThunk';

import { fetchAccountById, fetchCounterPartyAccountById } from "../../store/accountThunk";
import { fetchDocumentSettings } from '../../store/documentSettingStore/documentSettingThunk';
import { fetchAccountsByName } from "../../store/accountThunk";
import { EstimateFormInputs } from "../../utils/estimate.types";
import DocAttachInfo from '../DocumentAttachment/DocAttachInfo';
import PayTo from './PayTo';
import CreditNoteButtons from './CreditNoteFooterButtons';
import TemplateSelect from './TemplateSelect'
import { StyledBoxLoader } from './Invoice.style';
import { Utils } from "../../utils/common-utils";
import moment from 'moment';

import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { fetchStates } from '../../store/stateStore/stateThunk';
import TableInlineEdit from '../Table/TableInlineEdit';
import { AInvoice, AInvoices, InvoiceFormInputs } from '../../utils/invoice.types';
import { dispatchSale } from '../../store/estimateStore/estimateThunk';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { Padding } from '@mui/icons-material';

// Schema to validate account form.
const schema = yup.object().shape({
  invoiceNumber: yup.string().required("Credit Note Number is required"),
  refInvoiceNumber: yup.string().required("Please Search and Select Invoice"),
  refInvoiceId: yup.string().required("Please select Invoice from search"),
  reason: yup.string().required("Please select a reason"),
  client: yup.string().required("Please select Client"),
  account: yup.string().required("Account is required."),
  lineItem: yup.string().required("Minimum 1 Line Item required.")
});

// credit note reason options (must match backend enum)
const REASON_OPTIONS: IOptions[] = [
  { label: 'Sales Return', value: 'SALES_RETURN' },
  { label: 'Price Revision', value: 'PRICE_REVISION' },
  { label: 'Discount', value: 'DISCOUNT' },
  { label: 'Deficiency in Service', value: 'DEFICIENCY_IN_SERVICE' },
  { label: 'Order Cancellation', value: 'ORDER_CANCELLATION' },
  { label: 'Other', value: 'OTHER' },
];

interface CredtiNoteProps {
  type: string;
}

// Estimate component. 
const CreditNote: FC<CredtiNoteProps> = ({ type }) => {
  const { pathname } = useLocation();
  const { creditNoteId: creditNoteId } = useParams();
  const isEditing = creditNoteId ? true : false;

  // use form
  const methods = useForm<InvoiceFormInputs>({
    resolver: yupResolver(schema),
  });

  // States
  const [open, setOpen] = useState<Array<boolean>>([false, false, false, false]);
  const [valueDate, setValueDate] = useState<Date>(new Date());
  const [createDate, setCreateDate] = useState<Date>(new Date());
  const [estimateExpiryDate, setEstimateExpiryDate] = useState<Date>(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000));
  const [poDate, setPoDate] = useState<Date>();
  const [invoiceSeqData, setInvoiceSeqData] = useState<any>();
  const creditNoteDetails = useAppSelector(state => state.creditNotes.creditNoteDetails);
  const { documentSettings } = useAppSelector(state => state.documentSettings);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { countries } = useAppSelector(state => state.country);
  const { countryStates } = useAppSelector(state => state.countryState);
  const { docs } = useAppSelector(state => state.docAttach);
  const [inLineItems, setInLineItems] = useState<any[]>([]);
  const [payToObj, setPayToObj] = useState<any>();
  const [editInLineItems, setEditInLineItems] = useState<any[]>([]);
  const [docData, setDocData] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>();
  const [selectedBuyer, setSelectedBuyer] = useState<any>();
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [isClientLoading, setIsClientLoading] = useState(false);
  const [isBuyerLoading, setIsBuyerLoading] = useState(false);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isformLoaded, setIsFormLoaded] = useState(false);
  const [fetchData, setFetchData] = useState('');
  const [fetchDocs, setFetchDocs] = useState('');
  const [docDeleteId, setDocDeleteId] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
  const [payToAccount, setPayToAccount] = useState<any>();
  const formRef = useRef<any>();
  const [isDispatched, setIsDispatched] = useState(false);
  const [queryParams, setQueryParams] = useState('');
  const [documentSettingId, setDocumentSettingId] = useState('');
  const [newInvoiceId, setNewInvoiceId] = useState('');

  const [stateOptions, setStateOptions] = useState<IOptions[]>([]);
  const [selectedState, setSelectedState] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [reason, setReason] = useState("");
  const [selectedIsEditInvoiceDocNumber, setSelectedIsEditInvoiceDocNumber] = useState("");
  
  // search invoice vairables
  const elmRef = useRef<any>(null);
  const [invoiceOptions, setInvoiceOptions] =  useState<any[]>([]);
  const [selectedInvoiceP, setSelectedInvoiceP] = useState<any>('');
  // const [newOption, setNewOption] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const MODULE_TYPE = "sale-credit-note";

  const openCloseDrawer = (label: string) => {
    let openArray = [...open]
    if (label === 'document') {
      openArray[0] = !openArray[0];
    } else if (label === 'client') {
      openArray[1] = !openArray[1];
      setIsClientLoading(true);
    } else if (label === 'agent') {
      openArray[2] = !openArray[2];
      setIsAgentLoading(true);
    } else if (label === 'invoice') {
      alert("clicked");
      setIsAgentLoading(true);
    } else {
      openArray[3] = !openArray[3];
      setIsClientLoading(true);
    }
    setOpen(openArray);
  }

  const closeDialog = (isSaved: boolean) => {
    let openArray = [...open]
    openArray.forEach((x, i) => openArray[i] = false);
    setOpen(openArray);

    if (isSaved) {
      setFetchData(Math.random().toString());
    }
  }
  

  const closeDocAttach = (isSaved: boolean) => {
    if (isSaved) {
      setFetchDocs(Math.random().toString());
    }
  }

  const cancelDialog = () => {
    setIsClientLoading(false); setSelectedAgent(false); setIsAgentLoading(false); setIsLoading(false);
  }

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'estimateDate')
      setValueDate(newValue);
      else if (name === 'estimateExpiryDate')
      setEstimateExpiryDate(newValue);
      else
        setCreateDate(newValue);
    }
  };

  const rowOnChangeHandler = (data: any) => {
    setInLineItems(data);
  };

  const payToOnChangeHandler = (data: any) => {
    setPayToObj(data);
  };

  const selectedClientHandler = (data: any) => {
    setSelectedClient(data);
  };

  const selectedAgentHandler = (data: any) => {
    setSelectedAgent(data);
  };

  // Method to handle save and send click events
  const selectedDispatchHandler = async (data: any) => {
    console.log("selectedDispatchHandler called ===== ")
    let params = '';
    let currentValues = methods.getValues();
    console.log(currentValues);
    Object.entries(data).forEach(([key, value]) => {
      if (value)
        params += key + '=' + value + '&';
    });
    console.log(params);
    setQueryParams(params.slice(0, -1));
    methods.setValue('pdfTemplate', currentValues.pdfTemplate);
    console.log(formRef)
    if (formRef) {
      console.log("in form ref");
      setIsDispatched(true);
      console.log(formRef?.current)
      console.log(formRef?.current.submit)
      formRef?.current.submit.click();
      console.log("formRef?.current.submit.click() called");
    }
    // }
  };

  // Method to handle invoice form submit.
  const onSubmitForm = async (data: InvoiceFormInputs) => {
    console.log("onsubmit called =========>");
    if (data) {
      console.log(data);
      delete data.lineItem;
      delete data.agent;
      delete data.amountFormat;

      data.lineItems = Object.values(inLineItems);

      data.date = valueDate.toString();
      data.invoiceType = "Credit Note"

      if (type === MODULE_TYPE) {
        // Client is returning amount since AP
        data.supplier = selectedAccount._id;
        data.buyer = selectedClient._id;
        data.invoiceAccountRelation = 'AP';
      } else {
        // Client is taking extra amount since AR
        data.supplier = selectedClient._id;
        data.buyer = selectedAccount._id;
        data.invoiceAccountRelation = 'AP';
      }
      data.account = selectedAccount._id;
      let address = (type === MODULE_TYPE) ? selectedClient.addresses : selectedBuyer.addresses;
      let shippingAddress = address.find((item: any) => item.type === 'Shipping');
      let billingAddress = address.find((item: any) => item.type === 'Billing');
      data.billingAddress = (billingAddress) ? billingAddress._id : (type === MODULE_TYPE) ? selectedClient._id : selectedBuyer._id;
      data.shippingAddress = (shippingAddress) ? shippingAddress._id : (type === MODULE_TYPE) ? selectedClient._id : selectedBuyer._id;
      data.client = selectedClient._id;
      if (selectedAgent) data.agent = selectedAgent._id;
    }

    try {
      if (!isEditing) {
        await dispatch(addInvoiceCreditNote(data)).unwrap().then(async (data) => {
          if (data) {
            let _creditNoteId = data._id
            if (_creditNoteId) setNewInvoiceId(_creditNoteId)
          }
        });
      } else {
        if (creditNoteId) {
          console.log("Called Update API")
          await dispatch(updateInvoiceCreditNote({ id: creditNoteId, invoicePayload: data })).unwrap()
          setNewInvoiceId(creditNoteId)
        }
      }
    } catch (error: any) {
      // Empty catch block. 
    }
  };

  //Method to handle Cancel button click.
  const onCancel = () => {
    methods.reset();
    const cancelLink = `/sales`;
    navigate(cancelLink);
  };

  const getEstimateDetails = async () => {
    await dispatch(fetchInvoiceSeqNumber(MODULE_TYPE)).then(async (data: any) => {
      console.log(data);
      if (data.payload) {
        setIsFormLoaded(true);
        setIsLoading(true);
        setInvoiceSeqData(data.payload);
      } else
        methods.trigger("invoiceNumber");
    }).catch(e => {
      console.log(e);
    });
  };

  const docDelete = (id: any) => {
    setDocDeleteId(id)
  }

  useEffect(() => {
    dispatch(fetchStates()).unwrap().then(async (data) => {
      console.log(data);
      if (data) {
        if (data.length > 0) {
          await data.forEach(async (item: string | any) => {
            setStateOptions((stateOptions: any) => ([...stateOptions, { label: item.name, value: item._id }]));
          });
        }
      }
    });
  }, []);



  // fetch current estimate details on edit
  useEffect(() => {
    console.log("creditNote id found == " + creditNoteId);
    if (creditNoteId) {
      console.log("found 111 creditNote id found == " + creditNoteId);
      dispatch(fetchInvoiceCreditNoteById(creditNoteId));
    }
  }, [creditNoteId, dispatch]);


  useEffect(() => {
    if (docDeleteId) {
      let docs = docData.filter(doc => doc._id !== docDeleteId);
      setDocData([...docs]);
      setDocDeleteId('');
    }
  }, [docDeleteId])

  useEffect(() => {
    const navigateDispatch = async () => {
      if (newInvoiceId) {
        console.log("newInvoiceId true")
        if (isDispatched) {
          console.log("isDispatched true")
          let currentData = methods.getValues();
          await dispatch(dispatchSale(
            { saleId: newInvoiceId, saleType: "Credit Note", params: queryParams, dispatchPayload: { templateId: currentData.pdfTemplate } }))
            .then(async (data: any) => { 
              if (data.payload) onCancel(); 
            });
        } else {
          if (type == 'sale-credit-note') {
            navigate(`/sales/credit-note/edit/${newInvoiceId}`);
          } else {
            navigate(`/sales/delivery-note/edit/${newInvoiceId}`);
          }
        }
      }
    }
    navigateDispatch();
  }, [isDispatched, newInvoiceId, dispatch, methods])


  useEffect(() => {
    console.log("calling useEffect for setting address and client Name")
    console.log(selectedClient);

    if (selectedClient) {

      if (type === MODULE_TYPE) { setAddesses(selectedClient) }
      methods.setValue("client", selectedClient.name);
      setIsClientLoading(false);
    }

    if (selectedAgent) {
      methods.setValue("agent", selectedAgent.name);
      setIsAgentLoading(false);
    }
  }, [selectedClient, selectedAgent, methods, countries]);

  // Set edit estimate data in case of edit.
  useEffect(() => {
    const setInitialData = async () => {

      console.log("called setInitialData from main ");
      console.log(creditNoteDetails);
      console.log(isEditing);
      console.log("estimatedeails  setInitialData data printed ");
      try {
        if (creditNoteDetails || selectedInvoiceP) {
          console.log("called setInitialData from isEditing ");
          console.log(creditNoteDetails);
          console.log(isEditing);
          setIsFormLoaded(true);
          setIsClientLoading(true);
          setIsLoading(true);

          console.log("in Edit ===== ", selectedInvoiceP);

          if(selectedInvoiceP && typeof selectedInvoiceP === 'object' && selectedInvoiceP._id) {
            console.log("==== Cond1: Matched selectedInvoiceP && selectedInvoiceP not empty ===== ", selectedInvoiceP)
            methods.setValue('refInvoiceNumber', selectedInvoiceP.invoiceNumber || '');
            methods.setValue('refInvoiceId', selectedInvoiceP._id);
            methods.setValue("poNumber", selectedInvoiceP.poNumber);
            methods.setValue("poDate", selectedInvoiceP.poDate);
            methods.setValue('placeOfSupply', selectedInvoiceP.placeOfSupply);
            setPlaceOfSupply(selectedInvoiceP.placeOfSupply);
            // console.log('selectedInvoiceP?.client?._id: ', selectedInvoiceP?.client?._id);

            let selectedClient = await dispatch(fetchCounterPartyAccountById(selectedInvoiceP?.client?._id));
            setSelectedClient(selectedClient.payload);
            setInLineItems(selectedInvoiceP.lineItems);
            setEditInLineItems(selectedInvoiceP.lineItems);
          }

          if(creditNoteDetails && Object.keys(creditNoteDetails).length > 0) {
            console.log("==== Cond2: Matched only for few required fields creditNoteDetails =====" , creditNoteDetails)
            methods.setValue('invoiceNumber', creditNoteDetails.invoiceNumber);
            methods.setValue('date', creditNoteDetails.date);
            methods.setValue("account", selectedAccount.name);
            methods.setValue("terms", creditNoteDetails.terms);
            methods.setValue("footer1", creditNoteDetails.footer1);
            methods.setValue('pdfTemplate', creditNoteDetails.pdfTemplate);
            methods.setValue('reason', creditNoteDetails.reason || '');
            setReason(creditNoteDetails.reason || '');
            setInvoiceStatus(creditNoteDetails.status)
          }

          if(creditNoteDetails && (!selectedInvoiceP || !selectedInvoiceP._id)) {
            console.log("==== Cond3: Matched only creditNoteDetails && selectedInvoiceP is empty =====")
            methods.setValue('refInvoiceNumber', creditNoteDetails.refInvoiceNumber);
            methods.setValue('refInvoiceId', creditNoteDetails.refInvoiceId);
            methods.setValue("poNumber", creditNoteDetails.poNumber);
            methods.setValue("poDate", creditNoteDetails.poDate);
            methods.setValue('placeOfSupply', creditNoteDetails.placeOfSupply);
            setPlaceOfSupply(creditNoteDetails.placeOfSupply);
            console.log('creditNoteDetails?.client?._id: ', creditNoteDetails?.client?._id);

            let selectedClient = await dispatch(fetchCounterPartyAccountById(creditNoteDetails?.buyer?._id));
            setSelectedClient(selectedClient.payload);
            setInLineItems(creditNoteDetails.lineItems)
            setEditInLineItems(creditNoteDetails.lineItems)
          }

          // set the required fields from invoice ( buyer, placeOfSupply, billTo, ShipTo, LineItems)

          // if (isEditing && selectedIsEditInvoiceDocNumber == '') { 
          //   console.log("in edit from credit note edit", selectedInvoiceData.creditNumber);
          //   setSelectedIsEditInvoiceDocNumber(selectedInvoiceData.creditNumber);
          // };
          // methods.setValue('invoiceDocNumber', selectedInvoiceData.creditNumber);
          // // methods.setValue('invoiceNumber', selectedInvoiceData.invoiceNumber);
          // // methods.setValue('invoiceId', selectedInvoiceData._id);
          // methods.setValue("account", selectedAccount.name);
          // methods.setValue("terms", selectedInvoiceData.terms);
          // methods.setValue("footer1", selectedInvoiceData.footer1);
          // // methods.setValue("poNumber", selectedInvoiceData.poNumber);
          // // methods.setValue("poDate", selectedInvoiceData.poDate);
          // methods.setValue("paymentTerms", selectedInvoiceData.paymentTerms);
          // // methods.setValue('pdfTemplate', selectedInvoiceData.pdfTemplate);
          // methods.setValue('creditNoteDate', selectedInvoiceData.date);
          // methods.setValue('placeOfSupply', selectedInvoiceData.placeOfSupply);

          // console.log('placeOfSupply obj: ', selectedInvoiceData.placeOfSupply);
          // setPlaceOfSupply(selectedInvoiceData.placeOfSupply);

          // console.log('selectedInvoiceData?.client?._id: ', selectedInvoiceData?.client?._id);

          // let selectedClient = await dispatch(fetchCounterPartyAccountById(selectedInvoiceData?.client?._id));
          // setSelectedClient(selectedClient.payload);

          // setInLineItems(selectedInvoiceData.lineItems)
          // setEditInLineItems(selectedInvoiceData.lineItems)

          // setCreditNoteDate(selectedInvoiceData.date)

          // setInvoiceStatus(selectedInvoiceData.status)
          // setIsLoading(false);
          // if (estimateDetails.status !== 'DRAFT') onCancel();
          setIsFormLoaded(false);
          setIsClientLoading(false);
          setIsLoading(false);
          console.log("End of initial data form setup ")
        }
      } catch (error: any) {
        // console.log("setInitialData catch block called");
      }
    };

    if (Object.keys(creditNoteDetails).length > 0) setInitialData();
    if (selectedInvoiceP) {
      if (Object.keys(selectedInvoiceP).length > 0) setInitialData();
    }
  }, [methods, selectedInvoiceP, creditNoteDetails, dispatch, isEditing, selectedAccount, type]);

  /* Search Invoice and set to all fields */


  const onChangeHandler1 = async (event: any, value: any) => {
    console.log("======= Called onChangeHandler with value ====", value);

    if (value && typeof value === 'object' && value._id) {
      // full copy: seed ref + header fields; line items/client/placeOfSupply
      // are filled by the setInitialData effect (deps on selectedInvoiceP)
      setSelectedInvoiceP(value);
      methods.setValue('refInvoiceNumber', value.invoiceNumber || '');
      methods.setValue('refInvoiceId', value._id);
      methods.setValue('poNumber', value.poNumber || '');
      methods.setValue('poDate', value.poDate || '');
      methods.setValue('terms', value.terms || '');
      methods.setValue('footer1', value.footer1 || '');
      methods.setValue('placeOfSupply', value.placeOfSupply || '');
      if (value.placeOfSupply) setPlaceOfSupply(value.placeOfSupply);

      // populate buyer card from the invoice's buyer/client
      const buyerId = value?.buyer?._id || value?.client?._id;
      if (buyerId) {
        const buyer = await dispatch(fetchCounterPartyAccountById(buyerId));
        const buyerPayload: any = buyer?.payload;
        if (buyerPayload) {
          setSelectedBuyer(buyerPayload);
          setSelectedClient(buyerPayload);
          setAddesses(buyerPayload);
          methods.setValue('client', buyerPayload._id);
        }
      }
    } else {
      // Clear if value is string or null
      setSelectedInvoiceP('');
      methods.setValue('refInvoiceNumber', '');
      methods.setValue('refInvoiceId', '');
    }
  }

  const onInputChangeHandler1 = async (event: React.SyntheticEvent, value: string, reason?: string) => {
    // only search on real typing, not when a selection fills the input
    if (reason && reason !== 'input') return;
    // search by invoice number only (strip any " (buyer name)" suffix)
    const searchName = (value || '').split(' (')[0].trim();
    if (searchName && searchName.length > 0) {
      await dispatch(fetchInvoicesByInvNumber({ name: searchName, voucherType: 'Credit Note' })).unwrap().then((data) => {
        if (data) {
          console.log(data)
          const d = data.invoices.filter(function (item) {
            return item._id !== selectedAccount._id;
          });

          console.log("=========== filter data ==== ", d);

          setInvoiceOptions(d);
        }
      });
    }
  };

  const handleRenderOption1 = (e: any, b: any, c: any) => {
    console.log(b.buyer.name)
    // console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    return <div {...e} className='auto-item' key={Math.random()}>
      <div className='img-wrapper'>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          variant='square'
          style={{
            width: "30px",
            height: "30px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.buyer.name.toString().substr(0, 1).toUpperCase()}</Avatar>
      </div>
      <div><b style={{ 'marginRight': '4px' }}>
        {b.buyer.name} ({b.invoiceNumber})
      </b>
        {/* {!b.buyer.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#09b2ee' }}>dagdas</InfoIcon></Tooltip>}
         {b.buyer.counterPartyOfAccount && <Tooltip title="My customer / vendor"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>} */}
      </div>
    </div>
  };

  /* Search Invoice and set to all fields */


  useEffect(() => {
    console.log("useeffect called 1111");
    if (invoiceSeqData) {
      console.log("invoiceSeqdata is present")
      console.log(invoiceSeqData)
      if (type === MODULE_TYPE && !isEditing) {
        console.log(!isEditing);
        console.log("setting of creditNoteNumber");
        methods.setValue("invoiceNumber", invoiceSeqData.invoiceNumber);
        methods.setValue("terms", invoiceSeqData.termsAndConditions);
        methods.setValue("footer1", invoiceSeqData.footer1);
        setIsLoading(false)
      }
      setTimeout(() => setIsFormLoaded(false), 1000);
    }
  }, [invoiceSeqData, methods, type, selectedInvoiceP])

  useEffect(() => {
    console.log("useeffect called 99999")
    if (type === MODULE_TYPE) {
      if (documentSettings.length === 0 && isEditing)
        dispatch(fetchDocumentSettings(selectedAccount._id));

      if (documentSettings.length > 0) {
        let obj = documentSettings.find((o: any) => o.documentType.name.toLowerCase() === 'credit note');
        setDocumentSettingId(obj?._id || '');
      }
    }
  }, [selectedAccount, documentSettings, dispatch, type, isEditing])

  //Fetch current selected account .
  useEffect(() => {
    console.log("useeffect called if fetch data 8888 ");
    console.log(fetchData);
    if (fetchData)
      console.log("useeffect called from fetchdata ");
      getEstimateDetails();
  }, [dispatch, fetchData]);

  //Fetch all last estimate seq number and estimate details.
  useEffect(() => {
    console.log("useeffect called if  isEditing && 'account-receivable' 9999 ")
    if (!isEditing && type === MODULE_TYPE)
    console.log("calling getEstimateDetails from last ")
      getEstimateDetails();
  }, [isEditing]);

  const setAddesses = (object: any) => {
    methods.setValue("shippingAddress", '')
    methods.setValue("billingAddress", '')
    let address = object.addresses;
    let shippingAddress = address.find((item: any) => item.type === 'Shipping') || [];
    let billingAddress = address.find((item: any) => item.type === 'Billing') || [];
    if (shippingAddress) {
      let countryName = countries.find((item: any) => item._id === shippingAddress.country);
      let _shippingAdddress = [shippingAddress.city, shippingAddress.state, countryName?.name, shippingAddress.line1, shippingAddress.line2, shippingAddress.code];
      methods.setValue("shippingAddress", _shippingAdddress.filter(n => n).join(", "));
    }
    if (billingAddress) {
      let countryName = countries.find((item: any) => item._id === billingAddress.country);
      let _billingAddress = [billingAddress.city, billingAddress.state, countryName?.name, billingAddress.line1, billingAddress.line2, billingAddress.code];
      methods.setValue("billingAddress", _billingAddress.filter(n => n).join(", "));
    }
  };

  useEffect(() => {
    console.log("calling useEffect for setting address and client Name")
    console.log(selectedClient);

    if (selectedClient) {

      if (type === MODULE_TYPE) { setAddesses(selectedClient) }
      methods.setValue("client", selectedClient.name);
      setIsClientLoading(false);
    }

    if (selectedAgent) {
      methods.setValue("agent", selectedAgent.name);
      setIsAgentLoading(false);
    }

    

    if (placeOfSupply) {
      console.log("place of supply value 9999999   " + placeOfSupply);
      console.log(placeOfSupply);
      methods.setValue("placeOfSupply", placeOfSupply);
    }
  }, [selectedClient, placeOfSupply, selectedAgent, methods, countries]);

  useEffect(() => {
    if (!isEditing) {
      methods.setValue("account", selectedAccount.name);
      methods.setValue("date", valueDate.toString());
      methods.setValue("paymentTerms", "Net-30");
      if (type === MODULE_TYPE)
        setPayToAccount(selectedAccount)
    }
  }, [methods, selectedAccount, valueDate, isEditing, type]);

  useEffect(() => {
    console.log("updating InLineFields useeffect 10000000000")
    const updateInLineFeild = async () => {
      if (inLineItems.length > 0) {
        let initialSum = 0;
        let sumWithInitial = inLineItems.reduce((previousValue, currentValue) => previousValue + currentValue.total, initialSum);

        let taxAmount = 0;
        let tax1 = 0;
        let tax2 = 0;
        let tax3 = 0;
        inLineItems.forEach((item) => {
          taxAmount += item.amount;
          tax1 += item.sgsttotal;
          tax2 += item.cgsttotal;
          tax3 += item.igsttotal;
        })
        methods.setValue("lineItem", 'filled');
        methods.setValue("amount", sumWithInitial);
        methods.setValue("amountFormat", Utils.formatNumber(sumWithInitial, true));
        methods.setValue("taxAmount", Utils.formatNumber(taxAmount, true));
        methods.setValue("tax1", tax1.toString());
        methods.setValue("tax1Format", Utils.formatNumber(tax1.toString(), true));
        methods.setValue("tax2", tax2.toString());
        methods.setValue("tax2Format", Utils.formatNumber(tax2.toString(), true));
        methods.setValue("tax3", tax3.toString());
        methods.setValue("tax3Format", Utils.formatNumber(tax3.toString(), true));
      } else
        methods.setValue("lineItem", '');
    };

    updateInLineFeild();
  }, [inLineItems, methods]);

  useEffect(() => {
    if (docs.length > 0 && fetchDocs) {
      let len = docs.length;
      setDocData(d => [...d, docs[len - 1]]);
    }
  }, [docs, fetchDocs])

  useEffect(() => {
    if (type === 'account-payable') {
      setIsBuyerLoading(true);
      dispatch(fetchAccountsByName(selectedAccount.name)).unwrap().then((data) => {
        if (data) {
          data.accounts = data.accounts.filter(function (item) {
            return item._id == selectedAccount._id;
          });
          setSelectedBuyer(data.accounts[0]);
          setAddesses(data.accounts[0]);
          setIsBuyerLoading(false);
        }
      });
    }
  }, [selectedAccount])

  const handleChange = (event: SelectChangeEvent<any>) => {
    setSelectedState(event.target.value);

  };

  return (
    <StyledCard sx={{ pb: 5 }}>
      <FormProvider {...methods}>
        <CardContent className={'invoiceContent'}>
          <Typography className={'cardHeader'} variant="h4" component="div" style={{}}>
            <Box>{isEditing ? "Edit Credit Note" : "Create Credit Note"}</Box>
            <Box style={{ fontWeight: 'normal', alignItems: 'center', display: 'flex', flexDirection: 'row' }}>{(type === MODULE_TYPE) && "Credit Note Setup"}
              {(type === MODULE_TYPE) && <Tooltip title="Credit Note Setup"><SettingsIcon style={{ marginLeft: '5px', cursor: 'pointer' }} onClick={() => { openCloseDrawer('document') }} fontSize="small" /></Tooltip>}</Box>
          </Typography>
          <Box component="form" onSubmit={methods.handleSubmit(data => onSubmitForm(data))} ref={formRef} noValidate sx={{ mt: 0 }} style={{ position: 'relative' }}>
            {isformLoaded && <StyledBoxLoader component="div" sx={{ mt: 1 }}><CircularProgress color="inherit" /></StyledBoxLoader>}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container style={{ justifyContent: 'space-between', padding: '20px 10px', backgroundColor: '' }}>
              <Grid item md={3} xs={12}>
                  {isLoading ? <div></div> : <CustomTextField variant="standard" label="Invoice No." name="invoiceNumber" required InputProps={{ readOnly: (type === MODULE_TYPE || (isEditing && type === MODULE_TYPE)) ? true : false, disabled: (type === MODULE_TYPE) ? true : false }} className={(type === MODULE_TYPE) ? 'disabledField' : ''} />}
                </Grid>
                <Grid item md={3} xs={12}>
                  {isLoading ? <div></div> : <CustomTextField variant="standard" label="Ref Invoice Number" name="refInvoiceNumber" value={methods.watch('refInvoiceNumber') || ''} InputProps={{ readOnly: true }} className={'disabledField'} />
                  }
                </Grid>
                <Grid item md={2} xs={12}>
                  <Stack style={{ marginTop: '16px', marginBottom: '8px', flexDirection: 'row', alignItems: 'center' }}>
                    <DesktopDatePicker
                      label="Invoice Date"
                      inputFormat="dd/MM/yyyy"
                      maxDate={moment().add(1).toDate()}
                      value={valueDate}
                      onChange={(value) => { handleDateChange(value, 'valueDate') }}
                      renderInput={(params) => <TextField {...params} name={'valueDate'} required variant="standard" style={{ marginRight: (type === 'account-receivable') ? '10px' : '0px', flex: 1 }} />}
                    />
                  </Stack>
                </Grid>
                <Grid item md={4} xs={12}>
                  {isLoading ? <div></div> : <CustomTextField variant="standard" label="Ref Invoice Id" name="refInvoiceId" style={{ display: 'none' }} className={'hiddenText'}/>}

                  <Stack direction="row" style={{ alignItems: 'center' }} spacing={1}>
                    <Autocomplete
                      ref={elmRef}
                      id={`Search Ref Invoice`}
                      freeSolo
                      value={selectedInvoiceP || null}
                      onChange={onChangeHandler1}
                      onInputChange={onInputChangeHandler1}
                      options={invoiceOptions}
                      renderOption={handleRenderOption1}
                      getOptionLabel={(option) => option.invoiceNumber || ''}
                      renderInput={(params) => <CustomTextField {...params} variant="standard" name="search12" label={`Search Invoice`} />}
                      style={{ width: '100%' }}
                    />
                    <Tooltip title="Add new"><AddIcon style={{ cursor: 'pointer', marginTop: '16px' }} fontSize="medium" /></Tooltip>
                  </Stack>
                </Grid>
                
                <Grid item md={3} xs={12}>
                  <CustomSelect
                    name="reason"
                    label="Reason"
                    id="reason"
                    variant="standard"
                    required
                    options={REASON_OPTIONS}
                    value={reason ?? ''}
                    placeholder="Select Reason"
                    onChange={(event: SelectChangeEvent<any>) => {
                      methods.setValue("reason", event.target.value);
                      setReason(event.target.value);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container style={{ justifyContent: 'space-between', padding: '20px 10px', backgroundColor: '' }}>
                <Grid item md={6} xs={12}>
                  {(type === MODULE_TYPE) && <div style={{ display: 'flex' }}>
                    <Avatar
                      alt={selectedAccount.name || 'Picture'}
                      imgProps={{ "referrerPolicy": 'no-referrer' }}
                      src={selectedAccount.logo}
                      style={{ width: '60px', height: '60px', objectFit: 'contain', marginRight: '10px' }}
                    />
                    {isLoading ? <div></div> : <CustomTextField className={'disabledField'} label="Supplier" variant="standard" name="account" InputProps={{ readOnly: true, disabled: true }} />}
                  </div>}
                </Grid>
                <Grid item md={6} xs={12}>
                  {stateOptions && <CustomSelect
                    name="placeOfSupply"
                    label="Select Place of Supply"
                    id="state"
                    variant="standard"
                    options={stateOptions}
                    // value={selectedState}
                    value={placeOfSupply ?? ''}
                    placeholder="Select Place of Supply"
                    // onChange={(event: SelectChangeEvent<any>) => { handleChange(event) }} 

                    onChange={(event: SelectChangeEvent<any>) => {
                      methods.setValue("placeOfSupply", event.target.value);
                      setPlaceOfSupply(event.target.value);
                    }}
                    />
                  }
                </Grid>
                <Grid item md={6} xs={12}>
                  {(type === MODULE_TYPE) && <>
                    <Stack style={{ flexDirection: 'row', alignItems: 'center', marginTop: '0px' }}>
                      <Avatar
                        alt={selectedClient?.name || ''}
                        imgProps={{ "referrerPolicy": 'no-referrer' }}
                        src={selectedClient?.logo || ''}
                        style={{ width: '60px', height: '60px', objectFit: 'contain', marginRight: '10px' }}
                      />
                      {isClientLoading ? <div></div> : <CustomTextField className={'disabledField'} variant="standard" label={"Buyer"} name="client" required placeholder={'Needs to be selected from drawer'} InputProps={{ readOnly: true, disabled: true }} />}

                      <Tooltip title="Search or Add New Buyer"><SearchIcon style={{ cursor: 'pointer' }} onClick={() => { openCloseDrawer('buyer') }} fontSize="medium" /></Tooltip>
                    </Stack>
                  </>}
                </Grid>
                <Grid item md={6} xs={12}>
                  <TemplateSelect invoiceId={creditNoteId} invoiceStatus={invoiceStatus} invoiceDocType={'sale-credit-note'} />
                </Grid> 
                <Grid item md={6} xs={12}>
                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ }} variant="standard" multiline label={"Bill To"} name="billingAddress" required InputProps={{ readOnly: true, disabled: true }} />}
                </Grid>
                <Grid item md={6} xs={12}>
                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ }} variant="standard" multiline label={"Ship To"} name="shippingAddress" required InputProps={{ readOnly: true, disabled: true }} />}
                </Grid>
                
              </Grid>
              <TableInlineEdit onChangeRow={rowOnChangeHandler} rowsData={editInLineItems} />

              {/* <Typography>some text goess here {JSON.stringify(stateOptions)}</Typography> */}

              <Grid container sx={{ mt: 0 }} style={{ justifyContent: 'space-between', padding: '0px 6px' }}>
                <Grid item md={12} xs={12}><CustomTextField className={'hiddenText'} variant="standard" label="" name="lineItem" required /></Grid>
                <Grid item xs={8}>

                </Grid>
                <Grid item xs={4}>
                  <Grid item xs={12}>
                    <CustomTextField className={`disabledField text-right`} label="Taxable Amount" variant="standard" name="taxAmount" InputProps={{ readOnly: true, disabled: true }} />
                    <CustomTextField className={`disabledField text-right`} label="IGST" variant="standard" name="tax3Format" InputProps={{ readOnly: true, disabled: true }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax3" />
                    <CustomTextField className={`disabledField text-right`} label="CGST" variant="standard" name="tax2Format" InputProps={{ readOnly: true, disabled: true }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax2" />
                    <CustomTextField className={`disabledField text-right`} label="SGST" variant="standard" name="tax1Format" InputProps={{ readOnly: true, disabled: true }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax1" />
                    <CustomTextField className={`disabledField text-right`} label="Total" variant="standard" name="amountFormat" required InputProps={{ readOnly: true, disabled: true }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="amount" />
                  </Grid>
                </Grid>

                

                {(type === MODULE_TYPE) && <>
                  <Grid item md={12} xs={12}>
                    {isLoading ? <div></div> : <CustomTextField variant="standard" multiline label="Terms & Conditions" name="terms" />}
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {isLoading ? <div></div> : <CustomTextField variant="standard" multiline label="Footer" name="footer1" InputProps={{ readOnly: true, disabled: true }} className={'disabledField'} />}
                  </Grid>
                </>}
              </Grid>
            </LocalizationProvider>

            <CreditNoteButtons type={type} selectedSend={selectedDispatchHandler} invoiceStatus={invoiceStatus} onCancel={onCancel} />
            {/* {
              <LoadingButton
                className={'buttonContainedRoot'}
                type="submit"
                name={'submit'}
                loading={ status === ApiStatus.LOADING}
                // loading={isApiCalled}
                // disabled={!methods.formState.isValid}
                variant="contained"
                // onClick={handleClose}
              >
                {"Save as Draft 1"}
              </LoadingButton>
            } */}
          </Box>
        </CardContent>
      </FormProvider>

      <Drawer anchor="right" open={open[0]}>
        {<DialogDocumentSettingForm open={open[0]} setDocumentType={'invoice'} documentSettingId={documentSettingId} accountId={selectedAccount._id} onClose={closeDialog}></DialogDocumentSettingForm>}
      </Drawer>
      <Drawer anchor="right" open={open[3]} >
        {<DialogClientForm open={open[3]} title={'Buyer'} onClose={closeDialog} onCancel={cancelDialog} selectedData={selectedClientHandler}></DialogClientForm>}
      </Drawer>
    </StyledCard>
  );
}

export default CreditNote;